// ./src/utils/api/PollQuestionService.ts
//
// Event website poll / quiz question service (distinct from the RSVP-form question).
// OpResult-native, event-scoped: every list is filtered by eventId. Full single-item
// CRUD plus a bulk status change / category set-clear / remove (array of ids in the
// where clause). Talks to the eventPollQuestion* GraphQL operations.
//
// A question is single | multi | free_text. Choice types carry an `options` list
// ({ id, label, isCorrect }); `isQuiz` marks a graded question whose correct options
// score answers. Photos are a flat `images` gallery on the record (first = main),
// the same source-agnostic media model as signup items. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";
import { refusedNewInputs } from "@em/api";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the server POLL_QUESTION_TYPE boundary)
// =============================================================================

export const POLL_QUESTION_TYPE = {
  SINGLE: "single",
  MULTI: "multi",
  FREE_TEXT: "free_text",
} as const;

export type PollQuestionType = (typeof POLL_QUESTION_TYPE)[keyof typeof POLL_QUESTION_TYPE];

/** The choice types that carry an `options` list (free_text has none). */
export const POLL_CHOICE_TYPES: PollQuestionType[] = [POLL_QUESTION_TYPE.SINGLE, POLL_QUESTION_TYPE.MULTI];

export const isPollChoiceType = (type?: string | null): boolean =>
  !!type && POLL_CHOICE_TYPES.includes(type as PollQuestionType);

// =============================================================================
// GraphQL
// =============================================================================

const POLL_QUESTION_FIELDS = `
  id
  eventId
  accountId
  categoryId
  question
  description
  questionType
  options { id label isCorrect }
  allowAddOption
  isQuiz
  isRequired
  images { uploadedFileId url uploadedFile { id fileUrl thumbnailUrl } }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const POLL_QUESTION_LIST_QUERY = gql`
  query EventPollQuestionList($filter: EventPollQuestionFilter, $params: PaginationAndSorting) {
    eventPollQuestionList(filter: $filter, params: $params) {
      code
      data { ${POLL_QUESTION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLL_QUESTION_GET_QUERY = gql`
  query EventPollQuestionGet($id: ID) {
    eventPollQuestionGet(id: $id) {
      code
      data { ${POLL_QUESTION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLL_QUESTION_GET_MANY_QUERY = gql`
  query EventPollQuestionGetMany($ids: [ID]) {
    eventPollQuestionGetMany(ids: $ids) {
      code
      data { ${POLL_QUESTION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLL_QUESTION_CREATE_MUTATION = gql`
  mutation EventPollQuestionCreate($params: EventPollQuestionInput) {
    eventPollQuestionCreate(params: $params) {
      code
      data { ${POLL_QUESTION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLL_QUESTION_UPDATE_MUTATION = gql`
  mutation EventPollQuestionUpdate($where: EventPollQuestionWhereInput, $params: EventPollQuestionInput) {
    eventPollQuestionUpdate(where: $where, params: $params) {
      code
      data { ${POLL_QUESTION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLL_QUESTION_REMOVE_MUTATION = gql`
  mutation EventPollQuestionRemove($where: EventPollQuestionWhereInput) {
    eventPollQuestionRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventPollQuestion schema)
// =============================================================================

/** A resolvable file ref (from ms_storage) — freshest URL for a stored uploaded_file. */
export interface PollQuestionFileRef {
  id?: string | null;
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
}

/** One image on a poll question — an uploaded_file ref (upload/library) and/or a URL (stock/URL). */
export interface PollQuestionImage {
  uploadedFileId?: string | null;
  url?: string | null;
  /** Resolved file (federation) — present only for uploadedFileId images once the gateway is recomposed. */
  uploadedFile?: PollQuestionFileRef | null;
}

/** One answer choice; `isCorrect` only matters for quiz questions. */
export interface PollOption {
  id?: string | null;
  label?: string | null;
  isCorrect?: boolean | null;
}

export interface PollQuestion {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  question?: string | null;
  description?: string | null;
  /** single | multi | free_text. */
  questionType?: string | null;
  /** Answer choices (single/multi types); free_text has none. */
  options?: PollOption[] | null;
  /** Guests may propose their own option (choice types). */
  allowAddOption?: boolean | null;
  /** Graded quiz question (correct options score answers) vs an ungraded poll. */
  isQuiz?: boolean | null;
  /** Guests must answer this question to submit. */
  isRequired?: boolean | null;
  /** Flat photo gallery (first entry = main). */
  images?: PollQuestionImage[] | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

/** A single image's display URL: freshest resolved file URL, else the stored URL. */
export const pollQuestionImageUrl = (img?: PollQuestionImage | null): string | null =>
  img?.uploadedFile?.fileUrl || img?.url || null;

/** The main (first) image URL to display, or null when the question has no photos. */
export const pollQuestionMainImageUrl = (item?: PollQuestion | null): string | null =>
  pollQuestionImageUrl(item?.images?.[0]);

export interface PollQuestionImageInput {
  uploadedFileId?: string | null;
  url?: string | null;
}

export interface PollOptionInput {
  id?: string | null;
  label?: string | null;
  isCorrect?: boolean | null;
}

export interface PollQuestionInput {
  eventId?: string | null;
  categoryId?: string | null;
  question?: string | null;
  description?: string | null;
  questionType?: string | null;
  options?: PollOptionInput[] | null;
  /** #147 — on an update: the options as this drawer LOADED them, so the server can merge what guests did meanwhile. */
  optionsBase?: PollOptionInput[] | null;
  allowAddOption?: boolean | null;
  isQuiz?: boolean | null;
  isRequired?: boolean | null;
  /** Flat photo gallery (first entry = main). */
  images?: PollQuestionImageInput[] | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status or category (server-enforced).
export interface PollQuestionBulkInput {
  status?: number | null;
  /** Reassign every selected question's category, or null to clear it. */
  categoryId?: string | null;
}

export interface PollQuestionFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  questionType?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface PollQuestionWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

// ── The polls & quizzes report (#45) ─────────────────────────────────────────
// ONE server aggregate behind the whole page. Polls do NOT go through the signup family's
// `eventGuestSignupStats` — they have their own pair of tables, so this is their own read,
// closest in shape to the custom-answer report (#42).
const POLL_STATS_QUERY = gql`
  query EventPollStats($params: EventPollStatsInput) {
    eventPollStats(params: $params) {
      code
      data {
        eventId
        audience
        includeEmptyQuestions
        audienceGuests
        audienceHeads
        respondentGuests
        questionCount
        askedCount
        unansweredCount
        unlistedCount
        totalAnswers
        quizQuestionCount
        gradedQuestionCount
        ungradableQuizCount
        hasQuiz
        answersTruncated
        participation
        includeGuestRoster
        guestsAnswered
        guestsNotAnswered
        guestRosterTruncated
        questions {
          pollQuestionId
          question
          questionType
          categoryId
          isQuiz
          isRequired
          allowAddOption
          orderNo
          status
          isAsked
          answerCount
          guestCount
          isUnanswered
          isChoice
          isGraded
          ungradableReason
          correctCount
          correctShare
          options { optionId label isCorrect guestCount share }
          textAnswers { eventGuestId guestName value answeredAt }
        }
        guests {
          eventGuestId
          guestName
          email
          phone
          rsvpStatus
          partySize
          answerCount
          scoreCorrect
          scoreOutOf
          answers { pollQuestionId question chosen answerText isCorrect answeredAt }
        }
      }
      errors { name errors warnings }
    }
  }
`;

/** Which audience a guest report counts — mirrors QUESTION_REPORT_AUDIENCE on the server. */
export const POLL_REPORT_AUDIENCE = {
  HOLDING_PLACE: "holding_place",
  EVERYONE: "everyone",
} as const;

/**
 * Which guests the by-guest roster lists — mirrors POLL_REPORT_PARTICIPATION on the server.
 * ⚠ Its own vocabulary, NOT the signup report's `signed_up` / `not_signed_up`: "signed up" is the
 * wrong word for a vote, and the server refuses anything outside this set.
 */
export const POLL_REPORT_PARTICIPATION = {
  ALL: "all",
  ANSWERED: "answered",
  NOT_ANSWERED: "not_answered",
} as const;

/** One option on a question, with how many guests picked it. */
export interface PollOptionStat {
  optionId?: string | null;
  label?: string | null;
  /** ⚠ Null on an ordinary poll — there is no right answer, so draw no tick. */
  isCorrect?: boolean | null;
  /** GUESTS, not picks: one guest choosing three options counts once against each. */
  guestCount?: number | null;
  /** Percent of the guests who answered THIS question. ⚠ Never a percent of picks. */
  share?: number | null;
}

export interface PollTextAnswer {
  eventGuestId?: string | null;
  guestName?: string | null;
  value?: string | null;
  answeredAt?: string | null;
}

/** One poll or quiz question, with what guests actually answered. */
export interface PollQuestionStat {
  pollQuestionId?: string | null;
  question?: string | null;
  questionType?: string | null;
  categoryId?: string | null;
  isQuiz?: boolean | null;
  isRequired?: boolean | null;
  allowAddOption?: boolean | null;
  orderNo?: number | null;
  status?: number | null;
  /** False when the question is no longer asked but guests have already answered it. */
  isAsked?: boolean | null;
  answerCount?: number | null;
  guestCount?: number | null;
  isUnanswered?: boolean | null;
  isChoice?: boolean | null;
  /** True when the question CAN be marked: a quiz, with options, and an answer key set. */
  isGraded?: boolean | null;
  /** Why a quiz question could not be marked. Null when there is nothing wrong. */
  ungradableReason?: string | null;
  /** ⚠ NULL — never 0 — when the question could not be graded. 0 would read as a hard question. */
  correctCount?: number | null;
  correctShare?: number | null;
  options?: PollOptionStat[] | null;
  textAnswers?: PollTextAnswer[] | null;
}

export interface PollGuestAnswer {
  pollQuestionId?: string | null;
  question?: string | null;
  chosen?: string[] | null;
  answerText?: string | null;
  /** ⚠ Null on an ordinary poll — an opinion is neither right nor wrong. */
  isCorrect?: boolean | null;
  answeredAt?: string | null;
}

/** One guest, everything they answered, and their quiz score. */
export interface PollGuest {
  eventGuestId?: string | null;
  guestName?: string | null;
  email?: string | null;
  phone?: string | null;
  rsvpStatus?: string | null;
  partySize?: number | null;
  /** 0 means they are on the chase list. */
  answerCount?: number | null;
  /** Out of every GRADABLE question on the event. Null when none is gradable. */
  scoreCorrect?: number | null;
  scoreOutOf?: number | null;
  answers?: PollGuestAnswer[] | null;
}

export interface PollStats {
  eventId?: string | null;
  audience?: string | null;
  includeEmptyQuestions?: boolean | null;
  audienceGuests?: number | null;
  audienceHeads?: number | null;
  respondentGuests?: number | null;
  questionCount?: number | null;
  askedCount?: number | null;
  unansweredCount?: number | null;
  unlistedCount?: number | null;
  totalAnswers?: number | null;
  quizQuestionCount?: number | null;
  gradedQuestionCount?: number | null;
  /** Quiz questions that CANNOT be marked — free text, or no answer key yet. */
  ungradableQuizCount?: number | null;
  hasQuiz?: boolean | null;
  answersTruncated?: boolean | null;
  questions?: PollQuestionStat[] | null;
  participation?: string | null;
  includeGuestRoster?: boolean | null;
  guests?: PollGuest[] | null;
  guestsAnswered?: number | null;
  guestsNotAnswered?: number | null;
  guestRosterTruncated?: boolean | null;
}

export class PollQuestionService {
  /** The polls & quizzes report (#45). Every switch has a server-side default. */
  static async stats(
    eventId: string,
    options?: {
      audience?: string;
      includeEmptyQuestions?: boolean;
      participation?: string;
      /** The by-guest view. Left false, the server does not read the roster at all. */
      includeGuestRoster?: boolean;
    },
  ): Promise<OpResult> {
    return ApiService.executeQuery(POLL_STATS_QUERY, {
      params: {
        eventId,
        audience: options?.audience ?? POLL_REPORT_AUDIENCE.HOLDING_PLACE,
        includeEmptyQuestions: options?.includeEmptyQuestions ?? true,
        participation: options?.participation ?? POLL_REPORT_PARTICIPATION.ALL,
        includeGuestRoster: options?.includeGuestRoster ?? false,
      },
    });
  }

  static async list(
    filter?: PollQuestionFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(POLL_QUESTION_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: PollQuestionFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<PollQuestion>(PollQuestionService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(POLL_QUESTION_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(POLL_QUESTION_GET_MANY_QUERY, { ids });
  }

  static async create(params: PollQuestionInput): Promise<OpResult> {
    return ApiService.executeMutation(POLL_QUESTION_CREATE_MUTATION, { params });
  }

  static async update(where: PollQuestionWhereInput, params: PollQuestionInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(POLL_QUESTION_UPDATE_MUTATION, { where, params });

    // #147 — a gateway older than `optionsBase` refuses the whole save over it: save again without (the older merge).
    if (params.optionsBase !== undefined && refusedNewInputs(result, ["optionsBase"])) {
      const { optionsBase: _unknownToGateway, ...older } = params;

      return ApiService.executeMutation(POLL_QUESTION_UPDATE_MUTATION, { where, params: older });
    }

    return result;
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(POLL_QUESTION_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status / category on every selected question in one call (server restricts a multi-id update). */
  static async bulkUpdate(ids: string[], params: PollQuestionBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(POLL_QUESTION_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected question in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(POLL_QUESTION_REMOVE_MUTATION, { where: { id: ids } });
  }
}
